import AmericanExpressIcon from '@images/icons/cardTypes/amex.svg?react'
import DefaultCardIcon from '@images/icons/cardTypes/card-icon.svg?react'
import DinersIcon from '@images/icons/cardTypes/diners.svg?react'
import DiscoverIcon from '@images/icons/cardTypes/discover.svg?react'
import JCBIcon from '@images/icons/cardTypes/jcb.svg?react'
import MastercardIcon from '@images/icons/cardTypes/mastercard.svg?react'
import VisaIcon from '@images/icons/cardTypes/visa.svg?react'
import type { MerchantActivityResponse } from 'mmfintech-backend-api/types'
import { formatMoney, getCurrencyDecimals, isValidArray, tr } from 'mmfintech-commons'
import moment from 'moment'
import { useEffect } from 'react'
import { applyCurrencyDisplayOverrides, formatCurrencyForDisplay } from './currencyDisplay'

enum CardTypeEnum {
  // noinspection SpellCheckingInspection
  ELECTRON = 'ELECTRON',
  MAESTRO = 'MAESTRO',
  DANKORT = 'DANKORT',
  INTERPAYMENT = 'INTERPAYMENT',
  UNITONPAY = 'UNITONPAY',
  VISA = 'VISA',
  MASTERCARD = 'MASTERCARD',
  AMEX = 'AMEX',
  DINERS_CLUB = 'DINERS_CLUB',
  DISCOVER = 'DISCOVER',
  JCB = 'JCB',
  OTHER = 'OTHER'
}

function titleCase(str: string): string {
  const splitStr = str.toLowerCase().split(' ')
  for (let i = 0; i < splitStr.length; i++) {
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1)
  }
  return splitStr.join(' ')
}

export const fillActivityFields = (activity: MerchantActivityResponse, translatedActivity: string): string => {
  let result = translatedActivity

  if (isValidArray(activity?.activityFields) && translatedActivity.includes('{{')) {
    activity.activityFields.forEach(activityField => {
      let replaceValue = activityField.value
      if (replaceValue.includes('METADATA.') || replaceValue.includes('MERCHANTS.')) {
        replaceValue = tr(activityField.value, titleCase(activityField.value.split('.').pop()))
      }
      if (activityField.key.includes('AMOUNT')) {
        const [value, currency] = activityField.value.split(';')
        replaceValue = formatMoney(value, formatCurrencyForDisplay(currency), getCurrencyDecimals(currency))
      }
      result = result.replace(`{{${activityField.key}}}`, replaceValue)
    })
  }
  return applyCurrencyDisplayOverrides(result)
}

export const detectCardType = (number: number): CardTypeEnum => {
  var re = {
    [CardTypeEnum.ELECTRON]: /^(4026|417500|4508|4844|4913|4917)\d{10}$/,
    [CardTypeEnum.MAESTRO]: /^(5018|5020|5038|5612|5893|6304|6759|6761|6762|6763|0604|6390)\d+$/,
    [CardTypeEnum.DANKORT]: /^(5019)\d+$/,
    [CardTypeEnum.INTERPAYMENT]: /^(636)\d+$/,
    [CardTypeEnum.UNITONPAY]: /^(62|88)\d+$/,
    [CardTypeEnum.VISA]: /^4\d{12}(\d{3})?$/,
    [CardTypeEnum.MASTERCARD]:
      /^(?:(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12})$/,
    [CardTypeEnum.AMEX]: /^3[47][0-9]{5,}$/,
    [CardTypeEnum.DINERS_CLUB]: /^3(?:0[0-5]|[68][0-9])[0-9]{4,}$/,
    [CardTypeEnum.DISCOVER]: /^6(?:011|5[0-9]{2})[0-9]{3,}$/,
    [CardTypeEnum.JCB]: /^(?:2131|1800|35[0-9]{3})[0-9]{3,}$/
  }

  for (const key in re) {
    if (re[key].test(number)) {
      return key as CardTypeEnum
    }
  }

  return CardTypeEnum.OTHER
}

export const CardIssuerIcon = (number: number): React.FunctionComponent => {
  const cardType = detectCardType(number)

  const cardIcons = {
    [CardTypeEnum.VISA]: VisaIcon,
    [CardTypeEnum.AMEX]: AmericanExpressIcon,
    [CardTypeEnum.DINERS_CLUB]: DinersIcon,
    [CardTypeEnum.MASTERCARD]: MastercardIcon,
    [CardTypeEnum.DISCOVER]: DiscoverIcon,
    [CardTypeEnum.JCB]: JCBIcon
  }

  if (cardIcons[cardType]) return cardIcons[cardType]

  return DefaultCardIcon
}

export const IsCollapsedMenu = ({ setCollapsedMenu }) => {
  useEffect(() => {
    const targetNode = document.querySelector('.core-menu-wrapper')

    if (targetNode.classList.contains('collapsed')) setCollapsedMenu(true)

    const callback = mutationList => {
      for (const mutation of mutationList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          setCollapsedMenu(mutation.target.classList.contains('collapsed'))
        }
      }
    }

    const observer = new MutationObserver(callback)

    observer.observe(targetNode, {
      attributes: true,
      childList: false,
      subtree: false
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  return null
}

export const cutCryptoAddress = (address: string, firstChars: number = 6, lastChars: number = 4): string => {
  if (!address || address.length <= firstChars + lastChars) {
    return address
  }

  const start = address.slice(0, firstChars)
  const end = address.slice(-lastChars)

  return `${start}...${end}`
}

export const getOS = () => {
  const userAgent = navigator.userAgent.toLowerCase()
  if (userAgent.includes('win')) return 'Windows'
  if (userAgent.includes('mac')) return 'Mac'
  return 'Other'
}

export const getRecurringName = (recurringStep: string, startDate: string) => {
  if (!recurringStep || !startDate) return '-'

  switch (recurringStep) {
    case 'DAILY':
      return tr('FRONTEND.RECURRING.EVERY_DAY_ON_THE', 'Every day')
    case 'WEEKLY':
      return tr('FRONTEND.RECURRING.EVERY_WEEK_ON_THE', 'Every week on {date}').replace(
        '{date}',
        moment(startDate).format('dddd')
      )
    case 'BI_WEEKLY':
      return tr('FRONTEND.RECURRING.EVERY_2_WEEKS_ON_THE', 'Every 2 weeks on {date}').replace(
        '{date}',
        moment(startDate).format('dddd')
      )
    case 'MONTHLY':
      return tr('FRONTEND.RECURRING.EVERY_MONTH_ON_THE', 'Every month on the {date}').replace(
        '{date}',
        moment(startDate).format('Do')
      )
    case 'YEARLY':
      return tr('FRONTEND.RECURRING.EVERY_YEAR_ON_THE', 'Every year on {date}').replace(
        '{date}',
        moment(startDate).format('Do [of] MMMM')
      )
  }
}

/**
 * Normalizes the value returned from CoreSelect with isMultiply={true} to always be an array of primitive values.
 *
 * CoreSelect can return values in different formats:
 * - String/Number: When adding a single item to existing array (rare, depends on CoreSelect version)
 * - Array of objects: [{value: "x", label: "X"}] on first selection
 * - Array of mixed types: [{value: "x", label: "X"}, "y"] when combining selections
 * - Array of primitives: ["x", "y"] when removing items
 *
 * This function normalizes all formats to a consistent array of primitive values.
 *
 * @param value - The value returned from CoreSelect onChange handler
 * @param currentValue - The current value in state (only used when value is a single string/number, which is rare)
 * @returns Normalized array of primitive values (strings or numbers)
 *
 * @example
 * // First selection returns object
 * normalizeMultiSelectValue([{value: "BGR", label: "Bulgaria"}], [])
 * // Returns: ["BGR"]
 *
 * @example
 * // Adding to existing selection (if CoreSelect returns single value)
 * normalizeMultiSelectValue("USA", ["BGR"])
 * // Returns: ["BGR", "USA"]
 *
 * @example
 * // Mixed array from CoreSelect
 * normalizeMultiSelectValue([{value: "BGR", label: "Bulgaria"}, "USA"], [])
 * // Returns: ["BGR", "USA"]
 *
 * @example
 * // Array already normalized by CoreSelect
 * normalizeMultiSelectValue(["BGR", "USA"], [])
 * // Returns: ["BGR", "USA"]
 */
export const normalizeMultiSelectValue = <T = string | number>(
  value: T | T[] | Array<{ value: T; label: string }> | any,
  currentValue: T[] = []
): T[] => {
  // Handle array values - this is the most common case
  if (Array.isArray(value)) {
    // Extract primitive values from objects or keep primitives as-is
    return value.map(item => (typeof item === 'object' && item !== null && 'value' in item ? item.value : item)) as T[]
  }

  // Handle single primitive value (string or number)
  // This is rare - CoreSelect typically returns arrays
  if (typeof value === 'string' || typeof value === 'number') {
    return [...currentValue, value as T]
  }

  // Fallback to empty array for unexpected types
  return []
}
